import { 
  collection, 
  doc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  query, 
  where,
  Timestamp 
} from 'firebase/firestore';
import { db, auth } from './firebase';
import { UserPermission, PermissionLevel } from '../types';

const PERMISSIONS_COLLECTION = 'permissions';

// 권한 생성 (인원 등록 시 자동 호출)
export const createUserPermission = async (
  email: string, 
  name: string, 
  userId: string, 
  level: PermissionLevel = 'viewer'
): Promise<void> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('로그인이 필요합니다.');
    }

    const permissionData: Omit<UserPermission, 'id'> = {
      email,
      name,
      userId,
      level,
      canEdit: level === 'admin' || level === 'editor',
      grantedBy: currentUser.uid,
      grantedAt: Timestamp.now()
    };

    // 이메일을 document ID로 사용
    const permissionRef = doc(db, PERMISSIONS_COLLECTION, email);
    await setDoc(permissionRef, permissionData);
  } catch (error: any) {
    console.error('권한 생성 실패:', error);
    throw new Error(`권한 생성 실패: ${error.message}`);
  }
};

// 권한 레벨 업데이트
export const updateUserPermissionLevel = async (
  email: string, 
  level: PermissionLevel
): Promise<void> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('로그인이 필요합니다.');
    }

    const permissionRef = doc(db, PERMISSIONS_COLLECTION, email);
    await updateDoc(permissionRef, {
      level,
      canEdit: level === 'admin' || level === 'editor',
      grantedBy: currentUser.uid,
      grantedAt: Timestamp.now()
    });
  } catch (error: any) {
    console.error('권한 업데이트 실패:', error);
    throw new Error(`권한 업데이트 실패: ${error.message}`);
  }
};

// 관리자 권한 추가 (이메일만으로)
export const addAdminPermission = async (email: string): Promise<void> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('로그인이 필요합니다.');
    }

    const permissionData: Omit<UserPermission, 'id'> = {
      email,
      level: 'admin',
      canEdit: true,
      grantedBy: currentUser.uid,
      grantedAt: Timestamp.now()
    };

    const permissionRef = doc(db, PERMISSIONS_COLLECTION, email);
    await setDoc(permissionRef, permissionData);
  } catch (error: any) {
    console.error('관리자 권한 추가 실패:', error);
    throw new Error(`관리자 권한 추가 실패: ${error.message}`);
  }
};

// 권한 삭제
export const deleteUserPermission = async (email: string): Promise<void> => {
  try {
    const permissionRef = doc(db, PERMISSIONS_COLLECTION, email);
    await deleteDoc(permissionRef);
  } catch (error: any) {
    console.error('권한 삭제 실패:', error);
    throw new Error(`권한 삭제 실패: ${error.message}`);
  }
};

// 모든 권한 조회
export const getAllPermissions = async (): Promise<UserPermission[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, PERMISSIONS_COLLECTION));
    return querySnapshot.docs.map(doc => doc.data() as UserPermission);
  } catch (error: any) {
    console.error('권한 목록 조회 실패:', error);
    throw new Error(`권한 목록 조회 실패: ${error.message}`);
  }
};

// 사용자 권한 조회
export const getUserPermission = async (email: string): Promise<UserPermission | null> => {
  try {
    const q = query(
      collection(db, PERMISSIONS_COLLECTION),
      where('email', '==', email)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const doc = querySnapshot.docs[0];
    return doc.data() as UserPermission;
  } catch (error: any) {
    console.error('사용자 권한 조회 실패:', error);
    throw new Error(`사용자 권한 조회 실패: ${error.message}`);
  }
};

// 권한 레벨별 라벨
export const getPermissionLevelLabel = (level: PermissionLevel): string => {
  switch (level) {
    case 'admin':
      return '관리자';
    case 'editor':
      return '편집자';
    case 'viewer':
      return '열람자';
    default:
      return '알 수 없음';
  }
};

// 권한 레벨별 색상
export const getPermissionLevelColor = (level: PermissionLevel): 'error' | 'warning' | 'success' => {
  switch (level) {
    case 'admin':
      return 'error';
    case 'editor':
      return 'warning';
    case 'viewer':
      return 'success';
    default:
      return 'success';
  }
};
